import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { JobViewModel } from "../ViewModels/JobViewModel";
import { useViewModel } from "mvvm/View";
import { useAppSelector, useDarkMode, useThemeColors } from "redux/hooks";
import { getJobIDFromReplyTarget } from "../../../assets/utilities/PageUtils";
import { ThreadJobEntryView } from "./ThreadJobEntryView";

export function JobDetailView(props: { model: JobViewModel, tagList: string[], tagUpdater: (jobID: string, tags: string[]) => void, onEditModeChange?: (isEditMode: boolean) => void, scrollContainerRef?: React.RefObject<HTMLDivElement>, parentJobAlias?: string, onSelectedJobAnimationChange?: (isAnimating: boolean) => void, onAddQueryToggle?: (query?: string, queryName?: string) => void }) {

    const job = useViewModel(props.model);
    const { tagUpdater, onEditModeChange, scrollContainerRef, onSelectedJobAnimationChange, onAddQueryToggle } = props;
    const pageJobs = job.page.jobs;
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();

    const threadJobs = useMemo(() => {
        const allJobs = pageJobs;
        const jobMap = new Map<string, JobViewModel>();
        allJobs.forEach(j => jobMap.set(j.jobID, j));

        let rootJob: JobViewModel = job;
        const visited = new Set<string>();
        let parentID = getJobIDFromReplyTarget(job.replyTarget);
        while (parentID && !visited.has(parentID)) {
            visited.add(parentID);
            const parentJob = jobMap.get(parentID);
            if (!parentJob) {
                break;
            }
            rootJob = parentJob;
            parentID = getJobIDFromReplyTarget(parentJob.replyTarget);
        }

        const childrenMap = new Map<string, JobViewModel[]>();
        allJobs.forEach(j => {
            const rootParentID = getJobIDFromReplyTarget(j.replyTarget);
            if (!rootParentID) return;
            if (!childrenMap.has(rootParentID)) {
                childrenMap.set(rootParentID, []);
            }
            childrenMap.get(rootParentID)!.push(j);
        });

        childrenMap.forEach(children => children.sort((a, b) => a.startTime - b.startTime));

        const ordered: JobViewModel[] = [];
        const traverse = (current: JobViewModel) => {
            ordered.push(current);
            const children = childrenMap.get(current.jobID) || [];
            children.forEach(traverse);
        };
        traverse(rootJob);
        return ordered;
    }, [job, pageJobs]);

    const threadKey = useMemo(() => threadJobs.map(threadJob => threadJob.jobID).join("|"), [threadJobs]);

    const [editingJobs, setEditingJobs] = useState<Set<string>>(new Set());
    const [animatingJobs, setAnimatingJobs] = useState<Set<string>>(new Set());
    const [isThreadPrefetching, setIsThreadPrefetching] = useState(() =>
        threadJobs.some(threadJob => threadJob.status !== "RUNNING" && threadJob.outputState.status === "not-loaded")
    );

    const jobElementRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
    const questionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

    const registerJobRef = useCallback((jobID: string, element: HTMLDivElement | null) => {
        const refs = jobElementRefs.current;
        if (!element) {
            refs.delete(jobID);
        } else {
            refs.set(jobID, element);
        }
    }, []);

    const registerQuestionRef = useCallback((jobID: string, element: HTMLDivElement | null) => {
        const refs = questionRefs.current;
        if (!element) {
            refs.delete(jobID);
        } else {
            refs.set(jobID, element);
        }
    }, []);

    const handleEntryEditModeChange = useCallback((jobID: string, isEditMode: boolean) => {
        setEditingJobs((prev: Set<string>) => {
            const next = new Set(prev);
            if (isEditMode) {
                next.add(jobID);
            } else {
                next.delete(jobID);
            }
            return next;
        });
    }, []);

    const handleAnimationStateChange = useCallback((jobID: string, isAnimating: boolean) => {
        setAnimatingJobs((prev: Set<string>) => {
            const next = new Set(prev);
            if (isAnimating) {
                next.add(jobID);
            } else {
                next.delete(jobID);
            }
            // Notify if this is the selected job
            if (jobID === job.jobID) {
                onSelectedJobAnimationChange?.(isAnimating);
            }
            return next;
        });
    }, [job.jobID, onSelectedJobAnimationChange]);

    const shouldShowThreadLoader = isThreadPrefetching;

    useEffect(() => {
        onEditModeChange?.(editingJobs.size > 0);
    }, [editingJobs, onEditModeChange]);

    useEffect(() => {
        let cancelled = false;
        const pendingJobs = threadJobs.filter(threadJob => threadJob.status !== "RUNNING" && threadJob.outputState.status === "not-loaded");

        if (pendingJobs.length === 0) {
            setIsThreadPrefetching(false);
            return;
        }

        setIsThreadPrefetching(true);

        const loaders = pendingJobs
            .map(threadJob => threadJob.loadOutput())
            .filter(Boolean) as Promise<void>[];

        Promise.allSettled(loaders).then(() => {
            if (!cancelled) {
                setIsThreadPrefetching(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [threadJobs, threadKey]);

    // Track previous values to detect user-initiated selection and final answer arrival
    const prevJobIDRef = useRef<string | null>(null);
    const prevOutputStatusRef = useRef<string | null>(null);
    const isInitialMountRef = useRef<boolean>(true);

    // Scroll only when:
    // 1. User clicks on a job in the list view (jobID changes from a previously set value)
    // 2. Final answer arrives (outputState.status becomes "loaded")
    useEffect(() => {
        if (!scrollContainerRef?.current || shouldShowThreadLoader) {
            // Still update refs even if we can't scroll
            if (isInitialMountRef.current) {
                prevJobIDRef.current = job.jobID;
                prevOutputStatusRef.current = job.outputState.status;
                isInitialMountRef.current = false;
            }
            return;
        }

        // On initial mount, just initialize refs without scrolling
        if (isInitialMountRef.current) {
            prevJobIDRef.current = job.jobID;
            prevOutputStatusRef.current = job.outputState.status;
            isInitialMountRef.current = false;
            return;
        }

        const shouldScroll =
            // User clicked on a different job in list view
            (prevJobIDRef.current !== null && prevJobIDRef.current !== job.jobID) ||
            // Final answer just arrived
            (prevOutputStatusRef.current !== "loaded" && job.outputState.status === "loaded");

        // Always update refs
        prevJobIDRef.current = job.jobID;
        prevOutputStatusRef.current = job.outputState.status;

        if (!shouldScroll) {
            return;
        }

        const scrollToSelectedJob = () => {
            const questionElement = questionRefs.current.get(job.jobID) || jobElementRefs.current.get(job.jobID);
            if (!questionElement) return;
            questionElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            });
        };

        const timeoutId = window.setTimeout(scrollToSelectedJob, 120);
        return () => window.clearTimeout(timeoutId);
    }, [job.jobID, job.outputState.status, scrollContainerRef, shouldShowThreadLoader]);

    return (
        <div className="flex-1 flex-column">
            <style>{`
                .ant-pagination-options-size-changer .ant-select-item {
                    padding: 1px 6px !important;
                }
                .ant-pagination-options {
                    display: flex !important;
                    align-items: center !important;
                }
                .ant-pagination-options-size-changer {
                    margin-left: 8px !important;
                }
                .ant-pagination-options-size-changer .ant-select-dropdown {
                    z-index: 1050 !important;
                }
                .scrolling-table-container {
                    overflow: visible !important;
                }
                .ant-table-wrapper {
                    overflow: visible !important;
                }
                .ant-table-pagination {
                    position: relative !important;
                    z-index: 1 !important;
                }
                ${darkMode ? `
                    .ant-table {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-table-thead > tr > th {
                        background-color: ${themeColors.backgroundColor} !important;
                        border-bottom: 1px solid ${themeColors.borderColor} !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-table-tbody > tr > td {
                        border-bottom: 1px solid ${themeColors.borderColor} !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-table-tbody > tr:hover > td {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .ant-table-tbody > tr.ant-table-row-selected > td {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .ant-table-cell-scrollbar {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .ant-pagination-item {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .ant-pagination-item a {
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-pagination-item:hover {
                        border-color: ${themeColors.primaryColorLight} !important;
                    }
                    .ant-pagination-item:hover a {
                        color: ${themeColors.primaryColorLight} !important;
                    }
                    .ant-pagination-item-active {
                        background-color: ${themeColors.selectedBackground} !important;
                        border-color: ${themeColors.primaryColorLight} !important;
                    }
                    .ant-pagination-item-active a {
                        color: ${themeColors.primaryColorLight} !important;
                    }
                    .ant-pagination-prev,
                    .ant-pagination-next {
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-pagination-prev:hover,
                    .ant-pagination-next:hover {
                        color: ${themeColors.primaryColorLight} !important;
                    }
                    .ant-pagination-prev.ant-pagination-disabled,
                    .ant-pagination-next.ant-pagination-disabled {
                        color: ${themeColors.textColorMuted} !important;
                    }
                    .ant-pagination-options-size-changer .ant-select-selector {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColor} !important;
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-pagination-options-size-changer .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-pagination-options-size-changer .ant-select-arrow {
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-pagination-options-size-changer .ant-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                    }
                    .ant-pagination-options-size-changer .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .ant-pagination-options-size-changer .ant-select-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .ant-pagination-options-size-changer .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .ant-pagination-total-text {
                        color: ${themeColors.textColor} !important;
                    }
                ` : ''}
            `}</style>
            <div className="flex-1 flex-column scroll" style={{ padding: "0.5em 3em" }}>
                {shouldShowThreadLoader ? (
                    <div
                        style={{
                            minHeight: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: themeColors.textColorMuted,
                            fontStyle: "italic"
                        }}
                    >
                        Loading stream details...
                    </div>
                ) : (
                    threadJobs.map(threadJob => (
                        <ThreadJobEntryView
                            key={threadJob.jobID}
                            job={threadJob}
                            tagList={props.tagList}
                            tagUpdater={tagUpdater}
                            onEntryEditModeChange={handleEntryEditModeChange}
                            registerJobRef={registerJobRef}
                            registerQuestionRef={registerQuestionRef}
                            parentJobAlias={props.parentJobAlias}
                            onAnimationStateChange={(isAnimating) => handleAnimationStateChange(threadJob.jobID, isAnimating)}
                            onAddQueryToggle={onAddQueryToggle}
                        />
                    ))
                )}
            </div>
        </div>
    )
}